import { useState } from 'react';
import type { FormEvent } from 'react';
import { useAdmin } from '../../context/admin';
import { NAME_MAX, PERMISSIONS, PERMISSION_GROUPS, STANDARD_LAYOUT_ID, WELL_SCOPES } from '../../lib/access';
import type { NamedLayout, PermissionKey, Role, WellScope } from '../../lib/access';
import { deleteRole, saveRole } from '../../lib/api/admin';
import type { RoleInput } from '../../lib/api/admin';
import { ActionMessages, AdminShell } from './AdminShell';
import { plural, usePaging, useView } from './listing';
import { DeleteLink, Dialog, DialogButtons, DialogError, ListFoot, ViewToggle } from './parts';
import { useAdminAction } from './useAdminAction';

const EMPTY: RoleInput = {
  name: '',
  description: '',
  scope: 'many',
  permissions: ['overview.view', 'rtlogs.view', 'documents.view'],
  layoutId: STANDARD_LAYOUT_ID,
};

/** A role with no wells has nothing to read on one, so only these stay available. */
const PEOPLE_ONLY: PermissionKey[] = ['users.manage', 'welladmins.manage'];

/** One line of the list: a role the superadmin defined, or the built-in one. */
interface Row {
  key: string;
  role: Role | null;
  name: string;
  description: string;
  scope: string;
  layout: string;
  users: number | null;
}

/**
 * What each role may do, how many wells it reaches, and which layout its
 * people see. The role is the one place this is decided; a user only picks a
 * role and the wells, never single permissions, so two people with the same
 * role always see the same portal.
 */
export function Roles() {
  const { store } = useAdmin();
  const { busy, error, notice, run, clear } = useAdminAction();
  const [view, setView] = useView();

  /** null: closed. 0: a new role. Otherwise the id of the role being edited. */
  const [editing, setEditing] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<WellScope | 'all-scopes'>('all-scopes');

  const layoutName = (id: number) => store.layouts.find((l) => l.id === id)?.name ?? 'Standard';
  const scopeLabel = (key: WellScope) => WELL_SCOPES.find((s) => s.key === key)?.label ?? key;

  const rows: Row[] = [
    {
      key: 'superadmin',
      role: null,
      name: 'Superadmin',
      description: 'Creates companies, wells, roles and layouts. Cannot be changed here.',
      scope: 'Every well of every company',
      layout: layoutName(STANDARD_LAYOUT_ID),
      users: null,
    },
    ...store.roles.map((role) => ({
      key: String(role.id),
      role,
      name: role.name,
      description: role.description,
      scope: scopeLabel(role.scope),
      layout: layoutName(role.layoutId),
      users: store.users.filter((u) => u.roleId === role.id).length,
    })),
  ];

  const filtered = rows.filter((row) => {
    const q = search.trim().toLowerCase();
    if (q && !`${row.name} ${row.description}`.toLowerCase().includes(q)) return false;
    // The built-in role has no scope to filter by; it shows only when nothing is filtered.
    if (scope !== 'all-scopes' && row.role?.scope !== scope) return false;
    return true;
  });
  const paging = usePaging(filtered);
  const filter = (apply: () => void) => {
    apply();
    paging.first();
  };

  const current = editing ? store.roles.find((r) => r.id === editing) : undefined;

  function open(role: Role | null) {
    clear();
    setEditing(role?.id ?? 0);
  }

  async function onSave(draft: RoleInput) {
    const id = editing || null;
    const ok = await run(() => saveRole(id, draft), id ? `${draft.name.trim()} saved.` : `${draft.name.trim()} created.`);
    if (ok) setEditing(null);
  }

  async function onDelete(role: Role) {
    const ok = await run(() => deleteRole(role.id), `${role.name} deleted.`);
    if (ok) setEditing(null);
  }

  const mayDo = (row: Row) => {
    if (!row.role) return <span className="cmchip">Everything</span>;
    const held = PERMISSIONS.filter((p) => row.role?.permissions.includes(p.key));
    if (held.length === 0) return <span className="cmmuted">Nothing</span>;
    return held.map((p) => (
      <span key={p.key} className="cmchip">
        {p.label}
      </span>
    ));
  };

  const nameCell = (row: Row) =>
    row.role ? (
      <button type="button" className="cmlink" onClick={() => open(row.role)}>
        {row.name}
      </button>
    ) : (
      <strong>{row.name}</strong>
    );

  const actions = (row: Row) =>
    row.role ? (
      <button type="button" className="cmbtn cmbtn--small" onClick={() => open(row.role)}>
        Edit
      </button>
    ) : (
      <span className="cmtag cmtag--plain">Built in</span>
    );

  return (
    <AdminShell
      active="Roles"
      title="Role Management"
      count={`${rows.length} ${plural(rows.length, 'Role')}`}
      action={
        <button type="button" className="cmbtn cmbtn--primary" onClick={() => open(null)}>
          + Add Role
        </button>
      }
    >
      <div className="cmtools">
        <input
          type="search"
          className="cmsearch"
          placeholder="Search role name or purpose…"
          aria-label="Search role name or purpose"
          value={search}
          onChange={(e) => filter(() => setSearch(e.target.value))}
        />
        <select
          className="cmselect"
          aria-label="Well access"
          value={scope}
          onChange={(e) => filter(() => setScope(e.target.value as WellScope | 'all-scopes'))}
        >
          <option value="all-scopes">Well access: All</option>
          {WELL_SCOPES.map((s) => (
            <option key={s.key} value={s.key}>
              Well access: {s.label}
            </option>
          ))}
        </select>

        <span className="cmspacer" />
        <ViewToggle view={view} onChange={setView} />
      </div>

      <ActionMessages error={error} notice={notice} inDialog={editing !== null} />

      {filtered.length === 0 && <p className="cmempty">No role matches that search.</p>}

      {paging.shown.length > 0 && view === 'list' && (
        <div className="cmtable-wrap">
          <table className="cmtable">
            <thead>
              <tr>
                <th>Role name</th>
                <th>Well access</th>
                <th>May do</th>
                <th>Layout</th>
                <th>Users</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paging.shown.map((row) => (
                <tr key={row.key}>
                  <td>
                    <div className="cmname">
                      <span>
                        {nameCell(row)}
                        {row.description && <small className="cmdesc">{row.description}</small>}
                      </span>
                    </div>
                  </td>
                  <td>{row.scope}</td>
                  <td className="cmwrap">{mayDo(row)}</td>
                  <td>{row.layout}</td>
                  <td>{row.users == null ? '—' : <strong>{row.users}</strong>}</td>
                  <td>
                    <span className="cmactions">{actions(row)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {paging.shown.length > 0 && view === 'grid' && (
        <div className="cmgrid">
          {paging.shown.map((row) => (
            <article key={row.key} className="cmcard">
              <header>
                <span>
                  {nameCell(row)}
                  <small>{row.scope}</small>
                </span>
                {row.users != null && (
                  <span className="cmtag">
                    {row.users} {plural(row.users, 'user')}
                  </span>
                )}
              </header>
              {row.description && <p className="cmcard-text">{row.description}</p>}
              <dl>
                <div>
                  <dt>Layout</dt>
                  <dd>{row.layout}</dd>
                </div>
                <div>
                  <dt>May do</dt>
                  <dd className="cmchips">{mayDo(row)}</dd>
                </div>
              </dl>
              <footer className="cmactions">{actions(row)}</footer>
            </article>
          ))}
        </div>
      )}

      <ListFoot paging={paging} one="role" />

      {editing !== null && (
        <RoleDialog
          key={editing}
          role={current ?? null}
          layouts={store.layouts}
          busy={busy}
          error={error}
          onClose={() => setEditing(null)}
          onSave={(draft) => void onSave(draft)}
          onDelete={current ? () => void onDelete(current) : undefined}
        />
      )}
    </AdminShell>
  );
}

function RoleDialog({
  role,
  layouts,
  busy,
  error,
  onClose,
  onSave,
  onDelete,
}: {
  role: Role | null;
  layouts: NamedLayout[];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (draft: RoleInput) => void;
  onDelete?: () => void;
}) {
  const [draft, setDraft] = useState<RoleInput>(() =>
    role
      ? {
          name: role.name,
          description: role.description,
          scope: role.scope,
          permissions: [...role.permissions],
          layoutId: role.layoutId,
        }
      : EMPTY,
  );

  function toggle(key: PermissionKey, on: boolean) {
    setDraft((d) => ({
      ...d,
      permissions: on ? [...d.permissions, key] : d.permissions.filter((p) => p !== key),
    }));
  }

  function changeScope(scope: WellScope) {
    setDraft((d) => ({
      ...d,
      scope,
      permissions: scope === 'none' ? d.permissions.filter((p) => PEOPLE_ONLY.includes(p)) : d.permissions,
    }));
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    onSave(draft);
  }

  return (
    <Dialog
      title={role ? `Edit Role — ${role.name}` : 'New Role'}
      size="wide"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
      footer={<DialogButtons busy={busy} label={role ? 'Save Changes' : 'Create Role'} onClose={onClose} />}
    >
      <DialogError error={error} />

      <div className="cmfields cmfields--3">
        <label className="cmfield">
          <span>Role Name *</span>
          <input
            type="text"
            value={draft.name}
            maxLength={NAME_MAX}
            required
            autoFocus
            placeholder="e.g. Drilling Engineer"
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </label>
        <label className="cmfield">
          <span>Well Access</span>
          <select value={draft.scope} onChange={(e) => changeScope(e.target.value as WellScope)}>
            {WELL_SCOPES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          <small>{WELL_SCOPES.find((s) => s.key === draft.scope)?.hint}</small>
        </label>
        <label className="cmfield">
          <span>Layout</span>
          <select value={draft.layoutId} onChange={(e) => setDraft({ ...draft, layoutId: Number(e.target.value) })}>
            {layouts.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
          <small>The sections and tabs this role sees.</small>
        </label>
      </div>

      <label className="cmfield">
        <span>What This Role Is For</span>
        <textarea
          rows={2}
          value={draft.description}
          maxLength={300}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
      </label>

      <div className="cmperms">
        {PERMISSION_GROUPS.map((group) => (
          <fieldset key={group} className="cmgroup">
            <legend>{group}</legend>
            {PERMISSIONS.filter((p) => p.group === group).map((p) => {
              const unavailable = draft.scope === 'none' && !PEOPLE_ONLY.includes(p.key);
              return (
                <label key={p.key} className={unavailable ? 'cmcheck is-off' : 'cmcheck'}>
                  <input
                    type="checkbox"
                    checked={draft.permissions.includes(p.key)}
                    disabled={unavailable}
                    onChange={(e) => toggle(p.key, e.target.checked)}
                  />
                  <span>
                    {p.label}
                    {!p.enforced && <small className="cmtag cmtag--plain">no screen uses it yet</small>}
                  </span>
                </label>
              );
            })}
          </fieldset>
        ))}
      </div>

      {draft.scope === 'none' && (
        <p className="cminfo">
          With no wells there is nothing to open on one, so only the administration permissions can be given.
        </p>
      )}

      {role && onDelete && (
        <DeleteLink what="role" question={`Delete the role ${role.name}?`} busy={busy} onDelete={onDelete} />
      )}
    </Dialog>
  );
}
